/**
 * Смена статуса номера — ОТДЕЛЬНЫЙ экран с «Назад» (правило форм tenant-app):
 * рендерится вместо детали, кнопки «Сохранить»+«Назад» снизу. Для статуса
 * «Не в строю» (out_of_order) причина ОБЯЗАТЕЛЬНА (домен это валидирует;
 * фронт требует её до отправки). После успеха событие пишется в историю
 * статуса с источником «менеджер» + причиной (бэк, room_status_events).
 *
 * PATCH /v1/rooms/{id}/status — гейтится mgmt+housekeeper (#331). 403 — это
 * «нет прав», НЕ logout (токены не трогаем).
 */
import { useState } from 'react';
import { Button, Card } from '@hsc/ui';
import { ROOM_STATUSES, type RoomStatus } from '@hsc/types';
import { ROOM_STATUS_LABEL } from '../../lib/fleet-format.js';
import { useUpdateRoomStatus } from './useRooms.js';

export interface RoomStatusChangeScreenProps {
  roomId: string;
  number: string;
  currentStatus: RoomStatus;
  onBack: () => void;
  onDone: () => void;
}

export function RoomStatusChangeScreen({
  roomId,
  number,
  currentStatus,
  onBack,
  onDone,
}: RoomStatusChangeScreenProps): JSX.Element {
  const [status, setStatus] = useState<RoomStatus>(currentStatus);
  const [reason, setReason] = useState('');
  const mutation = useUpdateRoomStatus();

  const reasonRequired = status === 'out_of_order' || status === 'maintenance';
  const reasonMissing = reasonRequired && reason.trim() === '';

  const submit = (): void => {
    if (reasonMissing) return;
    mutation.mutate(
      { id: roomId, input: { status, ...(reason.trim() ? { notes: reason.trim() } : {}) } },
      { onSuccess: () => onDone() },
    );
  };

  return (
    <section data-testid="room-status-change">
      <div className="stays-head">
        <div>
          <div className="cb-crumbs">Номера / № {number} / Статус</div>
          <h1 className="hsc-page__title">Изменить статус — № {number}</h1>
        </div>
      </div>

      <Card>
        <div className="hsc-stack" style={{ gap: 12 }}>
          <label className="hsc-field">
            <span className="hsc-field__label">Новый статус</span>
            <select
              className="hsc-input"
              value={status}
              onChange={(e) => setStatus(e.target.value as RoomStatus)}
              data-testid="status-select"
            >
              {ROOM_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {ROOM_STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </label>

          <label className="hsc-field">
            <span className="hsc-field__label">
              Причина{reasonRequired ? ' (обязательно)' : ' (необязательно)'}
            </span>
            <input
              className="hsc-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={reasonRequired ? 'напр. ремонт ванной' : 'комментарий'}
              data-testid="status-reason"
            />
          </label>

          {reasonMissing && (
            <p role="alert" className="ta-muted" data-testid="status-reason-error">
              Для статуса «{ROOM_STATUS_LABEL[status]}» нужно указать причину.
            </p>
          )}
          {mutation.isError && (
            <p role="alert" data-testid="status-error">
              Не удалось сменить статус (возможно, переход недопустим). Проверьте статус и попробуйте снова.
            </p>
          )}

          <div className="hsc-stack" style={{ flexDirection: 'row', gap: 8 }}>
            <Button onClick={submit} disabled={mutation.isPending || reasonMissing} data-testid="status-save">
              Сохранить
            </Button>
            <Button variant="ghost" onClick={onBack} data-testid="status-back">
              Назад
            </Button>
          </div>
        </div>
      </Card>
    </section>
  );
}
